import React from 'react';
import { type FormsyInjectedProps } from 'formsy-react';
import { Chip, InputAdornment, styled, TextField, type TextFieldProps } from '@mui/material';

import { type LogsType } from '../../hooks/useLogs';

export interface BaseTextFieldProps<T>
	extends Omit<TextFieldProps, 'name' | 'value' | 'variant' | 'fullWidth' | 'disabled' | 'error'>,
		Pick<FormsyInjectedProps<T>, 'isFormSubmitted' | 'isPristine' | 'isValid'>,
		Partial<Pick<FormsyInjectedProps<T>, 'validationErrors' | 'errorMessage'>> {
	/** The field name */
	name: string;
	/** The field value */
	value?: T;
	/** If true the field can be edited */
	isEdit?: boolean;
	/** The component's start adornment */
	startAdornment?: React.ReactNode;
	/** The component's end adornment */
	endAdornment?: React.ReactNode;
	/** Logs properties */
	logProps?: LogsType;
	/** A function used to parse the field value to string */
	fieldParser?: (fieldValue: T) => React.ReactNode;
	/** The formsy validation dictionary */
	validations?: { [key: string]: any };
}

export interface FieldProps<T, V = T>
	extends Pick<
			BaseTextFieldProps<T | null>,
			| 'className'
			| 'name'
			| 'label'
			| 'helperText'
			| 'value'
			| 'isEdit'
			| 'startAdornment'
			| 'endAdornment'
			| 'logProps'
			| 'required'
			| 'isFormSubmitted'
			| 'isPristine'
			| 'isValid'
			| 'validations'
			| 'validationErrors'
			| 'errorMessage'
		>,
		Pick<FormsyInjectedProps<T | null>, 'setValue'> {
	/** Function used to update the model */
	sendChange: (name: string, newValue: T | null) => void;
	/** Function called before updating the model's value */
	beforeChange?: (newValue?: V) => V | void;
	/** Function called after updating the model's value */
	afterChange?: (newValue?: V) => void;
}

const BaseTextField = React.forwardRef<HTMLDivElement, BaseTextFieldProps<any>>((props, ref) => {
	const {
		name,
		label,
		isEdit,
		startAdornment,
		endAdornment,
		required,
		helperText,
		errorMessage,
		isFormSubmitted,
		isPristine,
		isValid,
		logProps,
		fieldParser,
		autoComplete = 'off',
		onClick,
		slotProps,
		InputLabelProps,
		InputProps,
		...otherProps
	} = props;

	const showError = !isValid && (isFormSubmitted || !isPristine);
	const hasLogs = logProps?.hasLogs(name);
	const logsCounter = logProps?.getLogsCounter(name);

	const openLogsDialog: React.MouseEventHandler<HTMLDivElement> = event => {
		event.preventDefault();
		logProps?.openLogsDialog(name, label, fieldParser);
	};

	const logsEndAdornment = <Chip className="BaseTextField-logs" label={logsCounter} size="small" color="primary" />;
	const innerEndAdornment = hasLogs ? logsEndAdornment : endAdornment;

	return (
		<TextField
			{...otherProps}
			ref={ref}
			name={name}
			label={label}
			variant="filled"
			fullWidth
			autoComplete={autoComplete}
			InputLabelProps={{
				...(InputLabelProps || {}),
				shrink: true
			}}
			InputProps={{
				...(InputProps || {}),
				hiddenLabel: !label,
				disableUnderline: hasLogs,
				readOnly: hasLogs,
				startAdornment: startAdornment ? (
					<InputAdornment position="start">{startAdornment}</InputAdornment>
				) : (
					InputProps?.startAdornment
				),
				endAdornment: innerEndAdornment ? (
					<InputAdornment position="end">{innerEndAdornment}</InputAdornment>
				) : (
					InputProps?.endAdornment
				)
			}}
			disabled={!isEdit && !hasLogs}
			helperText={showError ? errorMessage : helperText}
			required={required}
			error={showError}
			onClick={isEdit ? onClick : openLogsDialog}
		/>
	);
});

const StyledBaseTextField = styled(BaseTextField)(({ name, logProps, theme }) => ({
	cursor: logProps?.hasLogs(name) ? 'pointer' : 'inherit',
	'& > .MuiInputLabel-root': {
		cursor: 'inherit',
		'&.Mui-disabled': {
			color: theme.palette.text.secondary
		}
	},
	'& > .MuiInputBase-root': {
		cursor: 'inherit',
		'&.Mui-disabled': {
			cursor: 'inherit',
			backgroundColor: logProps?.hasLogs(name) ? 'initial' : 'transparent'
		},
		'& > .MuiInputBase-input': {
			cursor: 'inherit',
			color: theme.palette.text.primary,
			WebkitTextFillColor: theme.palette.text.primary,
			'&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
				WebkitAppearance: 'none',
				margin: 0
			},
			'&[type=number]': {
				MozAppearance: 'textfield'
			}
		},
		'& > .MuiInputAdornment-root > .BaseTextField-logs': {
			cursor: 'inherit'
		}
	}
}));

export default React.memo(StyledBaseTextField);
